// UserIdLayout.jsx
import React from 'react';
import { useLoaderData, Outlet } from 'react-router';

import { createInitialStoreWithRequest } from '../createInitialStore.js';
import fetchUserDetails from '../action/fetchUser.js';
import { legacySSR as legacySSRDebug } from '../resource/debug.js';
import getHttpProxyHeaders from '../resource/getHttpProxyHeaders.js';
import prepareUserProfileMeta from '../resource/prepareUserProfileMeta.js';
import generateCanonicalAndAlternates from '../resource/generateCanonicalAndAlternates.js';
import { EMPTY_ARRAY } from '../resource/defaults.js';
import useSSRDataMerge, { resetSSRDataMerge } from '../hook/useSSRDataMerge.js';
import MetaTagsHelmet from '../component/MetaTagsHelmet.jsx';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { SUPPORTED_LANGUAGES } from '../RemoteConfigKeys.js';
import {
  getUserPostFeedNameWithQuery,
  fetchFeedTotalCount,
} from '../resource/fetchFeedTotalCount.js';

const legacySSRLog = legacySSRDebug.extend('log');
const legacySSRError = legacySSRDebug.extend('error');

export const shouldRevalidate = ({ currentParams: curr, nextParams: next }) =>
  curr.listType !== next.listType || curr.publishStatus !== next.publishStatus;

export const loader = async ({
  context,
  params,
  request,
  i18nInstance,
  language,
}) => {
  const log = legacySSRLog.extend('UserIdLayout loader()');
  const errorLog = legacySSRError.extend('UserIdLayout loader()');
  log('start', { context, params, request });

  // Reset to re-merge SSR data
  resetSSRDataMerge();

  try {
    const httpProxyHeaders = getHttpProxyHeaders({
      request,
    });
    log('getHttpProxyHeaders', { httpProxyHeaders });

    const { store } = await createInitialStoreWithRequest({
      params,
      request,
    });
    log('createInitialStoreWithRequest', { store: store.getState().toJS() });

    // Get state and feed name to prepare for parallel API calls
    const state = store.getState();
    const feedNameWithQuery = getUserPostFeedNameWithQuery({
      state,
      userId: params.userId,
      listType: params.listType,
    });

    // Fetch user details and message count in parallel
    const [, messageCountResult] = await Promise.allSettled([
      store.dispatch(fetchUserDetails({ id: params.userId, httpProxyHeaders })),
      feedNameWithQuery
        ? fetchFeedTotalCount({ feedNameWithQuery, httpProxyHeaders })
        : Promise.resolve(null),
    ]);

    const finalState = store.getState();
    const storeData = finalState.toJS();
    log('fetched data', { store: storeData });

    // Extract message count from result
    const messageCount =
      messageCountResult.status === 'fulfilled' ? messageCountResult.value : 0;

    const availableLanguages = getRemoteConfigData(
      finalState,
      SUPPORTED_LANGUAGES
    );
    const canonicalPath = `/user/${params.userId}${params.listType ? `/${params.listType}` : ''}`;
    const { canonical, alternates } = generateCanonicalAndAlternates({
      canonicalPath,
      availableLanguages,
      language,
    });

    // Prepare meta data for SEO in loader
    const meta = await prepareUserProfileMeta({
      state: finalState,
      userId: params.userId,
      listType: params.listType,
      hostname: request?.headers?.get('host') || 'swag.live',
      includeJsonLd: true,
      canonical,
      alternates,
      messageCount,
      i18n: i18nInstance,
    });

    log('meta data prepared', { meta });

    return {
      data: { store: { users: storeData.users } },
      params,
      meta,
      staticContext: {
        requestHeaders: {
          hostname: request?.headers?.get('host') || 'swag.live',
        },
      },
    };
  } catch (error) {
    errorLog('error', { error, params });
    return { data: null, error, params };
  }
};

const UserIdLayout = () => {
  const { data, error, meta = EMPTY_ARRAY } = useLoaderData();

  useSSRDataMerge({
    componentName: 'UserIdLayout',
    loaderData: data,
    loaderError: error,
  });

  return (
    <>
      <MetaTagsHelmet metaTags={meta} />
      <Outlet />
    </>
  );
};

export default UserIdLayout;
